/**
 * Date Range Selector Component
 * Allows selecting date range for reports
 */

import { Calendar, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '@/i18n';

interface DateRangeSelectorProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  className?: string;
}

export function DateRangeSelector({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRefresh,
  isRefreshing,
  className,
}: DateRangeSelectorProps) {
  const { t } = useI18n();
  const handleQuickSelect = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);

    onStartDateChange(start.toISOString().split('T')[0]);
    onEndDateChange(end.toISOString().split('T')[0]);
  };

  return (
    <div className={clsx('flex flex-wrap items-center gap-3', className)}>
      {/* Quick Select Buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleQuickSelect(7)}
          className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          {t('reportsPage.dateRangeSelector.last7Days')}
        </button>
        <button
          onClick={() => handleQuickSelect(30)}
          className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          {t('reportsPage.dateRangeSelector.last30Days')}
        </button>
        <button
          onClick={() => handleQuickSelect(90)}
          className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          {t('reportsPage.dateRangeSelector.last90Days')}
        </button>
      </div>

      {/* Date Inputs */}
      <div className="flex items-center gap-2 ml-auto">
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <span className="text-gray-500">{t('reportsPage.dateRangeSelector.to')}</span>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Refresh Button */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              isRefreshing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
            )}
          >
            <RefreshCw
              className={clsx('w-4 h-4', isRefreshing && 'animate-spin')}
            />
          </button>
        )}
      </div>
    </div>
  );
}
